import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Badge, Button } from '@heroui/react';
import { apiClient } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useNotifications } from '@/contexts/NotificationContext';
import { useSecurityAlertsWatcher } from '@/features/alerts/hooks';
import { formatRelative } from '@/lib/format';
import {
  BellIcon, ExclamationTriangleIcon, XCircleIcon, CheckCircleIcon, InformationCircleIcon,
} from '@/icons';

interface QueueSummary { deferred: number; oldestDeferredAgeSeconds: number }
interface OpsAlert { id: string; severity: 'warning' | 'danger'; title: string; to: string }

/**
 * Notifications: operational health/queue alerts (polled) PLUS the in-app
 * notification centre (security alerts pushed by the alerts watcher, with an
 * unread counter). The watcher runs here because the bell is always mounted, so
 * new security alerts arrive as toasts + bell + desktop notifications anywhere.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  // Immediate security-alert notifications (security admins only; no-op otherwise).
  useSecurityAlertsWatcher();

  // Service-health alerts return when a routed platform health API exists
  // (config/features.ts → health); /health/ready is not reachable via the gateway.
  const { data: queue } = useQuery({
    queryKey: queryKeys.queue.summary,
    queryFn: async () => (await apiClient.get<QueueSummary>('/queue/summary')).data,
    refetchInterval: 60_000, retry: false,
  });

  const ops = useMemo<OpsAlert[]>(() => {
    const out: OpsAlert[] = [];
    if (queue && queue.deferred > 1000) out.push({ id: 'queue', severity: 'warning', title: `${queue.deferred} messages deferred`, to: '/queue' });
    if (queue && queue.oldestDeferredAgeSeconds > 14400) out.push({ id: 'queue-age', severity: 'warning', title: 'Deferred mail older than 4h', to: '/queue' });
    return out;
  }, [queue]);

  const badge = unreadCount + ops.length;
  const empty = badge === 0 && notifications.length === 0;

  const icon = (level: string) =>
    level === 'success' ? <CheckCircleIcon className="h-5 w-5 text-success" />
    : level === 'warning' ? <ExclamationTriangleIcon className="h-5 w-5 text-warning" />
    : level === 'error' ? <XCircleIcon className="h-5 w-5 text-danger" />
    : <InformationCircleIcon className="h-5 w-5 text-info" />;

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button isIconOnly variant="light" size="sm" aria-label={`Notifications: ${badge} unread`} className="text-fg-muted">
          <Badge content={badge} color="danger" isInvisible={badge === 0} shape="circle" size="sm">
            <BellIcon className="h-5 w-5" />
          </Badge>
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Notifications"
        className="w-96"
        topContent={
          notifications.length > 0 ? (
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-xs font-semibold text-fg-subtle">Security alerts</span>
              <button type="button" onClick={markAllRead} className="text-xs text-brand hover:underline">Mark all read</button>
            </div>
          ) : null
        }
      >
        {[
          ...notifications.slice(0, 8).map((n) => (
            <DropdownItem
              key={`n-${n.id}`}
              startContent={icon(n.level)}
              description={n.message ? `${n.message} · ${formatRelative(n.createdAt)}` : formatRelative(n.createdAt)}
              className={n.read ? '' : 'bg-brand/5'}
              onPress={() => { markRead(n.id); if (n.href) navigate(n.href); }}
            >
              {n.title}
            </DropdownItem>
          )),
          ...ops.map((a) => (
            <DropdownItem
              key={`o-${a.id}`}
              onPress={() => navigate(a.to)}
              startContent={a.severity === 'danger' ? <XCircleIcon className="h-5 w-5 text-danger" /> : <ExclamationTriangleIcon className="h-5 w-5 text-warning" />}
            >
              {a.title}
            </DropdownItem>
          )),
          ...(empty ? [
            <DropdownItem key="empty" isReadOnly startContent={<CheckCircleIcon className="h-5 w-5 text-success" />}>
              No alerts — everything looks healthy.
            </DropdownItem>,
          ] : []),
        ]}
      </DropdownMenu>
    </Dropdown>
  );
}
